// Copyright (c) Microsoft Corporation.
// Licensed under the MIT license.
import { useState, useEffect, useRef, SetStateAction, Dispatch } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import * as styles from '../styles/OpenAiWidget.styles';
import { Call } from '@azure/communication-calling';
import { connectCallAutomationToGroupCall } from '../utils/callAutomationUtils';
import {
  CallProvider,
  DeclarativeCallAgent,
  StatefulCallClient,
  createStatefulCallClient,
  CallAgentProvider,
  CallClientProvider
} from '@azure/communication-react';
import { OpenAiWidgetCallScreen } from './OpenAiWidgetCallScreen';
import { v1 as createGUID } from 'uuid';
import { LogoIcon, VideoIcon } from './constants';
import { AzureCommunicationTokenCredential, CommunicationUserIdentifier } from '@azure/communication-common';
import { AppConfigModel, fetchConfig, fetchTokenResponse } from '../utils/AppUtils';

type AgentMessage = {
  id: string;
  text: string;
  timestamp: string;
};

type AssessmentScore = {
  score: number;
  evidence: string;
};

type AssessmentResult = {
  summary: string;
  recommendation: 'proceed' | 'hold' | 'consider';
  scores: {
    csFundamentals: AssessmentScore;
    knowledge: AssessmentScore;
    technicalDepth: AssessmentScore;
    problemSolving: AssessmentScore;
    communication: AssessmentScore;
  };
  concerns: string[];
  nextSteps: string[];
  generatedAt: string;
};

export const OpenAiWidget = (props: {
  setIsOpenCallback: Dispatch<SetStateAction<boolean>>;
  interviewRef: string;
}): JSX.Element => {
  const { setIsOpenCallback, interviewRef } = props;
  const navigate = useNavigate();
  const location = useLocation();
  const basePath = location.pathname.startsWith('/app/v2') ? '/app/v2' : '/app/v1';
  const [callAgent, setCallAgent] = useState<DeclarativeCallAgent>();
  const [statefulCallClient, setStatefulCallClient] = useState<StatefulCallClient>();
  const [tokenCredential, setTokenCredential] = useState<AzureCommunicationTokenCredential>();
  const [userIdentifier, setUserIdentifier] = useState<CommunicationUserIdentifier>();

  const [isAgentSpeakingViewActive, setIsAgentSpeakingViewActive] = useState(false); // Renamed for clarity
  const [isLoading, setIsLoading] = useState(false);
  const [currentCall, setCurrentCall] = useState<Call | undefined>(undefined);
  const [clientConfig, setClientConfig] = useState<AppConfigModel>();
  const callAutomationStarted = useRef(false);
  const [agentMessages, setAgentMessages] = useState<AgentMessage[]>([]);
  const [serverCallId, setServerCallId] = useState<string | undefined>(undefined);
  const [assessment, setAssessment] = useState<AssessmentResult | undefined>(undefined);
  const [assessmentLoading, setAssessmentLoading] = useState(false);
  const [assessmentError, setAssessmentError] = useState<string | undefined>(undefined);
  const assessmentRequested = useRef<string | null>(null);

  if (!interviewRef) {
    throw new Error('interviewRef is not set');
  }

  useEffect(() => {
    const fetchData = async (): Promise<void> => {
      try {
        const config = await fetchConfig();
        setClientConfig(config);
      } catch (error) {
        console.error('Error fetching configuration:', error);
      }
    };
    fetchData();
  }, []);

  // Effect for Server-Sent Events
  useEffect(() => {
    if (!currentCall || !clientConfig) {
      return;
    }
    const eventSource = new EventSource(`${clientConfig?.callbackUri}/api/agentStatusEvents`);
    console.log('SSE: Connecting to server for agent status events...');
    eventSource.onmessage = (event) => {
      console.log('SSE: Received message from server:', event.data);
      try {
        const serverEvent = JSON.parse(event.data);
        if (serverEvent.type === 'agentConnectedStatus') {
          console.log('SSE: Agent connected status from server:', serverEvent.data);
          const { isConnected } = serverEvent.data;
          callAutomationStarted.current = !!isConnected;
          setIsLoading(!isConnected);
        } else if (serverEvent.type === 'aiTranscript') {
          const transcript = serverEvent.data?.transcript as string | undefined;
          const timestamp = (serverEvent.data?.timestamp as string | undefined) ?? new Date().toISOString();
          if (transcript) {
            console.log(transcript);
            setAgentMessages((prev) => [...prev, { id: `${Date.now()}-${prev.length}`, text: transcript, timestamp }]);
          }
        }
      } catch (error) {
        console.error('SSE: Error parsing message data:', error);
      }
    };

    eventSource.onerror = (error) => {
      console.error('SSE: EventSource failed:', error);
    };

    return () => {
      console.log('SSE: Closing EventSource connection.');
      eventSource.close();
    };
  }, [currentCall, clientConfig]);

  const simulateServerRequest = (): Promise<boolean> => {
    return new Promise((resolve) => {
      setTimeout(() => {
        resolve(true);
      }, 1500);
    });
  };

  useEffect(() => {
    (async () => {
      try {
        const { token, user } = await fetchTokenResponse();
        console.log('Token fetched: ', token);
        console.log('User fetched: ', user);
        setUserIdentifier(user);
        setTokenCredential(new AzureCommunicationTokenCredential(token));
      } catch (e) {
        console.error(e);
      }
    })();
  }, [setUserIdentifier]);

  useEffect(() => {
    if (statefulCallClient === undefined) {
      if (!userIdentifier) {
        return;
      }
      const statefulClient = createStatefulCallClient({ userId: userIdentifier });
      setStatefulCallClient(statefulClient);
      console.log('Stateful client created', statefulClient);
    }
  }, [userIdentifier, setStatefulCallClient, statefulCallClient]);

  useEffect(() => {
    if (callAgent === undefined && statefulCallClient && tokenCredential) {
      const createCallAgent = async (): Promise<void> => {
        const callAgent = await statefulCallClient.createCallAgent(tokenCredential);
        setCallAgent(callAgent);
      };
      createCallAgent();
    }
  }, [callAgent, statefulCallClient, tokenCredential]);

  useEffect(() => {
    if (!currentCall) {
      return;
    }

    const handleStateChange = async (): Promise<void> => {
      if (currentCall.state === 'Connected') {
        if (!callAutomationStarted.current) {
          callAutomationStarted.current = true;
          console.log('Connecting to call automation...');
          try {
            const id = await currentCall.info?.getServerCallId();
            if (id) {
              setServerCallId(id);
            }
            await connectCallAutomationToGroupCall(currentCall, interviewRef);
            console.log('Successfully connected to call automation.');
          } catch (e) {
            console.error('Error connecting to call automation:', e);
            callAutomationStarted.current = false;
          }
        }
      }

      if (currentCall.state === 'Disconnected') {
        console.log('Call ended, resetting state.');
        setIsAgentSpeakingViewActive(false);
        setAgentMessages([]);
        callAutomationStarted.current = false;
        setCurrentCall(undefined);
        setIsLoading(false);
        const id = serverCallId ?? (await currentCall.info?.getServerCallId());
        if (id && assessmentRequested.current !== id && interviewRef) {
          assessmentRequested.current = id;
          await fetchAssessment(interviewRef);
        }
      }
      console.log('Current call state:', currentCall.state);
    };

    currentCall.on('stateChanged', handleStateChange);
    return () => currentCall.off('stateChanged', handleStateChange);
  }, [currentCall, serverCallId]);

  const handleGetAdviceClick = async (): Promise<void> => {
    console.log('Starting call with agent...');

    resetForNewCall();
    setAgentMessages([]);
    const callTemp = callAgent?.join({ groupId: createGUID() });
    setCurrentCall(callTemp);
    setIsLoading(true);
    const success = await simulateServerRequest();
    console.log('Successfully created a call');
    console.log('StatefulCallClient:', statefulCallClient);
    console.log('CallAgent:', callAgent);
    if (success) {
      setIsAgentSpeakingViewActive(true); // Activate the agent speaking view
    } else {
      alert("Sorry, we couldn't connect to the expert right now.");
    }
  };

  const fetchAssessment = async (id: string): Promise<void> => {
    setAssessmentLoading(true);
    setAssessmentError(undefined);
    try {
      const response = await fetch(`/api/assessment/${encodeURIComponent(id)}`);
      if (response.status === 401) {
        window.dispatchEvent(new CustomEvent('auth-unauthorized'));
        throw new Error('Unauthorized');
      }
      if (!response.ok) {
        throw new Error(`Failed to fetch assessment: ${response.statusText}`);
      }
      const result: AssessmentResult = await response.json();
      setAssessment(result);
    } catch (error: any) {
      console.error(error);
      setAssessmentError('Could not generate assessment yet. Please try again in a moment.');
    } finally {
      setAssessmentLoading(false);
    }
  };

  const closeAll = (): void => {
    setIsAgentSpeakingViewActive(false);
    setAgentMessages([]);
  };

  const resetForNewCall = (): void => {
    setAssessment(undefined);
    setAssessmentError(undefined);
    setServerCallId(undefined);
    assessmentRequested.current = null;
    setIsLoading(false);
    setIsAgentSpeakingViewActive(false);
    setAgentMessages([]);
    setCurrentCall(undefined);
    callAutomationStarted.current = false;
  };

  if (isAgentSpeakingViewActive && statefulCallClient && currentCall && callAgent && !isLoading) {
    return (
      <CallClientProvider callClient={statefulCallClient}>
        <CallAgentProvider callAgent={callAgent}>
          <CallProvider call={currentCall}>
            <OpenAiWidgetCallScreen currentCall={currentCall} onHangup={closeAll} messages={agentMessages} />
          </CallProvider>
        </CallAgentProvider>
      </CallClientProvider>
    );
  }

  if (assessment || assessmentLoading || assessmentError) {
    return (
      <div style={styles.expertCardStyle}>
        <h2 style={styles.titleStyle}>Interview assessment</h2>
        {assessmentLoading && <p style={styles.subtitleStyle}>Generating assessment...</p>}
        {assessmentError && <p style={{ ...styles.subtitleStyle, color: '#a80000' }}>{assessmentError}</p>}
        {assessment && (
          <div style={{ width: '100%', textAlign: 'left', marginTop: 8 }}>
            <p style={{ margin: '4px 0', fontWeight: 600 }}>{assessment.summary}</p>
            <p style={{ margin: '4px 0' }}>
              Recommendation:{' '}
              <span style={{ fontWeight: 700 }}>
                {assessment.recommendation === 'proceed'
                  ? 'Proceed'
                  : assessment.recommendation === 'hold'
                    ? 'Hold'
                    : 'Consider'}
              </span>
            </p>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr', gap: 8 }}>
              {[
                { key: 'csFundamentals', label: 'CS fundamentals' },
                { key: 'knowledge', label: 'Knowledge' },
                { key: 'technicalDepth', label: 'Technical depth' },
                { key: 'problemSolving', label: 'Problem solving' },
                { key: 'communication', label: 'Communication' }
              ].map((item) => {
                const scoreBlock = (assessment.scores as any)[item.key] as AssessmentScore;
                return (
                  <div
                    key={item.key}
                    style={{
                      background: '#f3f2f1',
                      borderRadius: 8,
                      padding: 8,
                      border: '1px solid #e1dfdd'
                    }}
                  >
                    <p style={{ margin: '0 0 4px 0', fontWeight: 700 }}>
                      {item.label}: {scoreBlock?.score ?? '–'}/5
                    </p>
                    <p style={{ margin: 0, color: '#605e5c' }}>{scoreBlock?.evidence ?? 'No evidence provided.'}</p>
                  </div>
                );
              })}
            </div>
            {assessment.concerns?.length > 0 && (
              <div style={{ marginTop: 8 }}>
                <p style={{ margin: '0 0 4px 0', fontWeight: 700 }}>Concerns</p>
                <ul style={{ margin: 0, paddingLeft: 16 }}>
                  {assessment.concerns.map((c, idx) => (
                    <li key={idx} style={{ color: '#a80000' }}>
                      {c}
                    </li>
                  ))}
                </ul>
              </div>
            )}
            {assessment.nextSteps?.length > 0 && (
              <div style={{ marginTop: 8 }}>
                <p style={{ margin: '0 0 4px 0', fontWeight: 700 }}>Suggested next steps</p>
                <ul style={{ margin: 0, paddingLeft: 16 }}>
                  {assessment.nextSteps.map((n, idx) => (
                    <li key={idx}>{n}</li>
                  ))}
                </ul>
              </div>
            )}
            <p style={{ margin: '8px 0 0 0', fontSize: 12, color: '#605e5c' }}>
              Generated at {new Date(assessment.generatedAt).toLocaleString()}
            </p>
          </div>
        )}
        <button
          style={{
            ...styles.getAdviceButtonStyle,
            ...(!callAgent && styles.getAdviceButtonDisabledStyle),
            ...(isLoading && styles.getAdviceButtonLoadingStyle)
          }}
          disabled={!callAgent || isLoading}
          onClick={() => {
            navigate(`${basePath}/interview`);
          }}
        >
          Finish
        </button>
      </div>
    );
  }

  function setIsOpen(isOpen: boolean): void {
    throw new Error('Function not implemented.');
  }

  // Default: "Talk to an expert" card
  return (
    <div style={styles.expertCardStyle}>
      <h2 style={styles.titleStyle}>Start Interview</h2>
      <button
        style={{
          ...styles.getAdviceButtonStyle,
          ...(isLoading && styles.getAdviceButtonLoadingStyle),
          ...(!callAgent && styles.getAdviceButtonDisabledStyle)
        }}
        onClick={handleGetAdviceClick}
        disabled={!callAgent || isLoading}
      >
        {isLoading ? (
          'Connecting...'
        ) : !callAgent ? (
          'Initializing...'
        ) : (
          <>
            <VideoIcon /> Start Interview
          </>
        )}
      </button>
    </div>
  );
};
